import debounce from 'lodash.debounce';

import getWireTransferCountries from '#shared/util/getWireTransferCountries.js';
import isObject from '#shared/util/isObject.js';

import cContentLayout from '#themes/default/default/layouts/contentLayout/contentLayout.js';
import cFormInput from '#themes/default/default/components/formInput/formInput.js';
import cFormLine from '#themes/default/default/components/formLine/formLine.js';
import cInput from '#themes/default/default/components/input/input.js';
import cInputGroup from '#themes/default/default/components/inputGroup/inputGroup.js';
import cSelect from '#themes/default/default/components/select/select.js';
import cSvg from '#themes/default/default/components/svg/svg.js';
import cTooltip from '#themes/default/default/components/tooltip/tooltip.js';

import template from './billingForm.html' with { type: 'html' };

export default {
	name: 'CBillingForm',
	components: {
		cContentLayout,
		cFormInput,
		cFormLine,
		cInput,
		cInputGroup,
		cSelect,
		cSvg,
		cTooltip
	},
	props: {
		customer: {
			type: Object,
			default: () => null
		},
		form: {
			type: Object,
			default: () => null
		},
		order: {
			type: Object,
			default: () => null
		},
		paymentType: {
			type: String,
			default: ''
		},
		providerAccount: {
			type: Object,
			default: () => null
		},
		selectedPayment: {
			type: Object,
			default: () => {}
		},
		showOnlyPayment: {
			type: Boolean,
			default: false
		},
		theme: {
			type: Object,
			default: () => null
		},
		url: {
			type: Object,
			default: () => null
		}
	},
	data() {
		return {
			inputGroupErrors: [],
			inputGroupNote: '',
			inputGroupWarning: '',
			lastZipCode: '',
			isZipCodeActive: false,
			isCountryTouched: false
		};
	},
	computed: {
		phoneNumber: {
			get() {
				return this.form.phoneNumber;
			},
			set(value) {
				this.setPhoneNumber(value);
			}
		},
		phoneExample() {
			// Phone number placeholder for India (@todo if we need more locales in the future, introduce some general solution - JSON file or config option?)
			return this.url.locale === 'en-in' ? '+91 12345 67890' : '';
		},
		zipCode: {
			get() {
				return this.customer.billing.zip;
			},
			set(value) {
				this.setZipCode(value);
			}
		},
		zipCodeSuggestion() {
			return this.isCustomId['CART-3170'] ? this.t('cart3170_form_suggestionZipCode') : '';
		},
		isWireTransfer() {
			return isObject(this.selectedPayment) && this.selectedPayment.type === 'wireTransfer';
		},
		isCountryDisabledByCustomID() {
			// CustomID that disables a country selector for every payment method
			return this.isCustomId['COUNTRYOFF'];
		},
		initialCountry() {
			// Get initial country from "open cart" API v2 call ("form" property)
			const initialCountryValue = this.form.initial?.countryCode;
			const initialCountry = this.countryList.find((country) => country.value === initialCountryValue);
			return initialCountry?.value;
		},
		isCountryDisabled() {
			const isEuroWireTransfer = this.isWireTransfer && this.order.currency === 'EUR';
			if (isEuroWireTransfer && !this.isCountryDisabledByCustomID) {
				this.inputGroupNote = this.t('form_noteCountryWireTransfer');
				return false;
			}
			// Clear the Wire Transfer note (on method change)
			this.inputGroupNote = '';
			// Disable country selector if it's disabled on PM's, config's or customID's (COUNTRYOFF) level
			return this.selectedPayment.hasFixedCountry || this.order.disableCountrySelector || this.isCountryDisabledByCustomID || Boolean(this.initialCountry);
		},
		countryList() {
			const countryList = this.order.countryList;
			const wtEuroCountryList = getWireTransferCountries(countryList);
			// Return all countries or filtered country list for WT
			return this.isWireTransfer && !this.isCountryDisabled && wtEuroCountryList.length ? wtEuroCountryList : countryList;
		},
		selectedCountry() {
			// Get country from GeoIP (online payments only)
			const formCountryValue = this.form.defaultCountry;
			const formCountry = this.countryList.find((country) => country.value === formCountryValue);
			// Get fallback country from region or current cart provider
			const urlLanguageValue = this.url.language;
			const urlCountryValue = this.url.region;
			const [, providerCountryValue] = this.theme.provider.locale.split('_');
			const fallbackCountryValue = this.form.fallbackCountry || (urlCountryValue && urlCountryValue !== 'ww' && urlLanguageValue !== 'qa' ? urlCountryValue : providerCountryValue).toUpperCase();
			// Get provider's country in case the fallback country or URL country isn't in the countryList
			const fallbackCountry = this.countryList.find((country) => country.value === fallbackCountryValue) || this.countryList.find((country) => country.value === providerCountryValue);
			// If current payment method has disabled country selector, use GeoIP country or url country
			if (this.isCountryDisabled) {
				return this.initialCountry || fallbackCountry.value;
			}
			// If customer made some interaction with the form, use selected billing country
			if (this.isCountryTouched) {
				return this.customer?.billing.country ?? fallbackCountry.value;
			}
			// Create a safety net for the error CART-4945
			const safetyNetCountry = 'US';
			// Use initial country or GeoIP country or url country as a default value
			return this.initialCountry || formCountry?.value || fallbackCountry?.value || safetyNetCountry;
		}
	},
	watch: {
		selectedPayment: debounce(function() {
			this.changeBillingCountry(this.selectedCountry);
		})
	},
	methods: {
		setPhoneNumber(phoneNumber) {
			// Set phone number that we're going to display
			this.$store.commit('SET_FORM', {
				phoneNumber
			});
			// Set phone number that is going to be send (without + symbol)
			this.$store.commit('SET_CUSTOMER', {
				billing: {
					...this.customer.billing,
					phoneNumber
				}
			});
		},
		setZipCode(zipCode) {
			let zip = zipCode.replace(/\s/g, '');
			// Add a hyphen at the 3rd position (Required in Japan)
			if (zip.charAt(3) !== '-' && zip.length > 3 && this.customer.billing.country === 'JP') {
				zip = `${zip.slice(0, 3)}-${zip.slice(3)}`;
			}
			this.$store.commit('SET_CUSTOMER', {
				billing: {
					...this.customer.billing,
					zip
				}
			});
		},
		changeBillingCountry(selectedCountryValue) {
			const selectedCountry = this.countryList.find((country) => country.value === selectedCountryValue) ?? {};
			const updatedCustomer = Object.assign({}, this.customer);
			updatedCustomer.billing.country = selectedCountry.value ?? this.customer.billing.country;
			updatedCustomer.billing.countryName = selectedCountry.label ?? this.customer.billing.countryName;
			this.$store.commit('SET_CUSTOMER', updatedCustomer);
		}
	},
	template
};
